/**
 * Vysúvacia lišta „Posledné faktúry" (.638) – v sekcii ESHOP, vždy po ruke
 * pri pravom okraji obrazovky.
 *
 * Oto: „sprav v eshope vysúvaciu lištu vždy dostupnú z pravej strany
 * monitora, kde po kliknutí bude náhľad prijatých posledných faktúr. Tam si
 * bude kolegyňa sledovať, čo aktuálne poprichádzalo, resp. čo sa naposledy
 * nahrávali aké faktúry."
 *
 * .953 Oto: „Mirke, keď ju otvorí v e-shope a má otvorenú aj objednávku, ju
 * prekrýva … možno by bolo super, keby to mala na opozitnej strane." Detail
 * objednávky sa vysúva sprava – v rozhraní „len ESHOP“ (bez ľavého menu) je lišta preto predvolene VĽAVO a tlačidlom
 * ⇄ v hlavičke sa dá preložiť na druhú stranu (pamätá sa v prehliadači).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, ChevronLeft, ChevronRight, FileText, Mail, RefreshCw, Upload } from 'lucide-react';
import { api, money } from '../api';

const kedy = (t) => {
  const d = new Date(`${String(t || '').replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return '';
  const dnes = new Date();
  const cas = d.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === dnes.toDateString()) return `dnes ${cas}`;
  const vcera = new Date(dnes); vcera.setDate(dnes.getDate() - 1);
  if (d.toDateString() === vcera.toDateString()) return `včera ${cas}`;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${cas}`;
};

function stav(d) {
  if (d.state === 'nahrany') return ['číta sa', 'bg-slate-100 text-slate-600'];
  if (d.state === 'chyba') return ['nepodarilo sa prečítať', 'bg-red-100 text-red-700'];
  if (d.prijem_stav === 'otvoreny') return ['v dodávke', 'bg-sky-100 text-sky-800'];
  if (d.prijem_id) return ['vybavená', 'bg-emerald-100 text-emerald-800'];
  return ['na kontrolu', 'bg-amber-100 text-amber-800'];
}

export default function PosledneFakturyLista({ predvolene = 'vpravo' }) {
  const [otvorena, setOtvorena] = useState(() => { try { return localStorage.getItem('eshop_faktury_lista') === '1'; } catch { return false; } });
  const [d, setD] = useState(null);
  const [nacitavam, setNacitavam] = useState(false);
  /* .640: klik na faktúru → jednoduchý zoznam produktov (kód, názov, značka, kusy). */
  const [detail, setDetail] = useState(null);
  const otvorDetail = (x) => {
    setDetail({ id: x.id, supplier_name: x.supplier_name, doc_number: x.doc_number, polozky: null });
    api.get(`/api/invoices/${x.id}/polozky-kratko`).then(setDetail).catch((e) => setDetail((d) => ({ ...d, chyba: e.message, polozky: [] })));
  };

  const nacitaj = () => {
    setNacitavam(true);
    api.get('/api/invoices/posledne?limit=40').then(setD).catch(() => {}).finally(() => setNacitavam(false));
  };
  useEffect(() => { nacitaj(); }, []);
  /* Otvorená sa obnovuje každú minútu, zatvorená len počet raz za päť. */
  useEffect(() => {
    const t = setInterval(nacitaj, otvorena ? 60_000 : 300_000);
    return () => clearInterval(t);
  }, [otvorena]);
  const [strana, setStrana] = useState(() => { try { const v = localStorage.getItem('eshop_faktury_strana'); return v === 'vpravo' || v === 'vlavo' ? v : predvolene; } catch { return predvolene; } });
  const vlavo = strana === 'vlavo';
  const prelozit = () => { const n = vlavo ? 'vpravo' : 'vlavo'; setStrana(n); try { localStorage.setItem('eshop_faktury_strana', n); } catch { /* nič */ } };
  const prepni = (v) => { setOtvorena(v); try { localStorage.setItem('eshop_faktury_lista', v ? '1' : '0'); } catch { /* nič */ } };

  return (
    <>
      {!otvorena && (
        <button onClick={() => prepni(true)}
          className={`fixed ${vlavo ? 'left-0 rounded-r-xl' : 'right-0 rounded-l-xl'} top-1/2 z-40 max-md:top-auto max-md:bottom-36 max-md:px-1 max-md:py-2 max-md:opacity-80 flex flex-col items-center gap-1 bg-slate-800 px-1.5 py-3 text-xs font-medium text-white shadow-lg hover:bg-slate-700`}
          title="Posledné prijaté faktúry">
          <FileText size={16} />
          <span style={{ writingMode: 'vertical-rl' }} className="rotate-180 max-md:hidden">Faktúry</span>
          {d?.dnes > 0 && <span className="rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-slate-900">{d.dnes}</span>}
        </button>
      )}
      {otvorena && (
        <aside className={`fixed bottom-0 top-0 z-40 flex w-[22rem] max-w-[90vw] flex-col border-slate-200 bg-white shadow-2xl ${vlavo ? 'left-0 border-r' : 'right-0 border-l'}`}>
          <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
            <FileText size={16} className="text-slate-600" />
            <div className="font-semibold text-slate-800">Posledné faktúry</div>
            {d?.dnes > 0 && <span className="rounded-full bg-amber-100 px-2 text-xs text-amber-800">dnes {d.dnes}</span>}
            <button className="ml-auto rounded p-1 text-slate-500 hover:bg-slate-100" onClick={nacitaj} title="Obnoviť">
              <RefreshCw size={14} className={nacitavam ? 'animate-spin' : ''} />
            </button>
            <button className="rounded p-1 text-slate-500 hover:bg-slate-100" onClick={prelozit}
              title={vlavo ? 'Presunúť lištu doprava' : 'Presunúť lištu doľava'}>
              <ArrowLeftRight size={14} />
            </button>
            <button className="rounded p-1 text-slate-500 hover:bg-slate-100" onClick={() => prepni(false)} title="Zavrieť">
              {vlavo ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          </div>
          {detail ? (
            <div className="flex-1 overflow-auto">
              <button className="flex w-full items-center gap-1 border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
                onClick={() => setDetail(null)}>
                <ChevronLeft size={16} /> späť na faktúry
              </button>
              <div className="px-3 pt-2">
                <div className="font-semibold text-slate-800">{detail.supplier_name || 'Doklad'}</div>
                <div className="text-xs text-slate-500">č. {detail.doc_number || '—'}</div>
              </div>
              {!detail.polozky ? <div className="p-3 text-sm text-slate-400">Načítavam…</div>
                : detail.chyba ? <div className="p-3 text-sm text-red-700">{detail.chyba}</div>
                  : !detail.polozky.length ? <div className="p-3 text-sm text-slate-400">Faktúra zatiaľ nemá položky (ešte sa číta alebo sa nepodarilo prečítať).</div>
                    : (
                      <table className="mt-2 w-full text-xs">
                        <thead className="text-left text-slate-500">
                          <tr className="border-b border-slate-200"><th className="px-3 py-1">Kód</th><th className="py-1">Názov</th><th className="py-1">Značka</th><th className="px-3 py-1 text-right">Ks</th></tr>
                        </thead>
                        <tbody>
                          {detail.polozky.map((p) => (
                            <tr key={p.id} className={`border-b border-slate-100 align-top ${p.sluzba ? 'text-slate-400' : 'text-slate-700'}`}>
                              <td className="whitespace-nowrap px-3 py-1 font-mono">{p.kod || '—'}</td>
                              <td className="py-1">{p.nazov}</td>
                              <td className="py-1">{p.znacka || ''}</td>
                              <td className="px-3 py-1 text-right font-medium">{Number(p.kusy).toLocaleString('sk-SK')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
            </div>
          ) : (
          <div className="flex-1 overflow-auto">
            {!d ? <div className="p-4 text-sm text-slate-400">Načítavam…</div> : !d.docs.length ? (
              <div className="p-4 text-sm text-slate-400">Zatiaľ žiadne faktúry.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {d.docs.map((x) => {
                  const [st, cls] = stav(x);
                  return (
                    <li key={x.id} className="cursor-pointer px-3 py-2 text-sm hover:bg-slate-50" onClick={() => otvorDetail(x)}>
                      <div className="flex items-baseline gap-2">
                        <span className="truncate font-medium text-slate-800">{x.supplier_name || x.file_name || `doklad ${x.id}`}</span>
                        <span className="ml-auto shrink-0 text-xs text-slate-400">{kedy(x.created_at)}</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600">
                        <span>{x.dobropis ? 'dobropis' : x.sluzba ? 'služba' : 'faktúra'} {x.doc_number || '—'}</span>
                        {x.total_net != null && <span className="font-medium">{money(x.total_net)} bez DPH</span>}
                        {x.poloziek > 0 && <span className="text-slate-400">{x.poloziek} pol.</span>}
                        <span className={`rounded px-1.5 py-0.5 ${cls}`}>{st}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                        {x.posta_od ? <><Mail size={10} /> mailom od {x.posta_od}</> : <><Upload size={10} /> nahral{x.nahral ? ` ${x.nahral}` : 'i ručne'}</>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          )}
          <Link to="/prijem-tovaru" className="border-t border-slate-200 px-3 py-2 text-center text-sm font-medium text-brand-700 hover:bg-slate-50">
            Otvoriť Príjem tovaru →
          </Link>
        </aside>
      )}
    </>
  );
}
